import React, { useCallback, useEffect, useState } from 'react';
import { TagIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import type { InvoiceGroup } from '../types';

const slugify = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export const InvoiceGroupsSettings: React.FC = () => {
  const [groups, setGroups] = useState<InvoiceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getInvoiceGroups();
    if (!res.error && res.data) setGroups(res.data.groups || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addGroup = async () => {
    const name = newName.trim();
    if (!name) return;
    const res = await apiService.createInvoiceGroup({
      name,
      slug: slugify(name),
      color: '#6366f1',
      sortOrder: groups.length + 1,
    });
    if (res.error) {
      setMessage(res.message || res.error || 'No se pudo crear el grupo');
      return;
    }
    setNewName('');
    setMessage('Grupo creado');
    await load();
  };

  const saveGroup = async (group: InvoiceGroup) => {
    const res = await apiService.updateInvoiceGroup(group.id, {
      name: group.name,
      slug: group.slug,
      color: group.color,
      sortOrder: group.sortOrder,
    });
    setMessage(res.error ? (res.message || res.error) : 'Grupo guardado');
    if (!res.error) await load();
  };

  const removeGroup = async (id: string) => {
    if (!window.confirm('¿Desactivar este grupo? Se quitará de las facturas asignadas.')) return;
    const res = await apiService.deleteInvoiceGroup(id);
    setMessage(res.error ? (res.message || res.error) : 'Grupo desactivado');
    if (!res.error) await load();
  };

  if (loading) return <p className="text-sm text-gray-500">Cargando grupos…</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#425466] dark:text-[#8b949e]">
        Etiquetas para clasificar facturas (Kit Digital, Entik Media, Conciertos…). Una factura puede tener varios grupos.
      </p>

      {message ? (
        <p className={`text-sm px-3 py-2 rounded ${
          message.includes('No se') || message.includes('error') || message.includes('Error')
            ? 'text-red-700 bg-red-50 dark:bg-red-900/20'
            : 'text-green-700 bg-green-50 dark:bg-green-900/20'
        }`}>{message}</p>
      ) : null}

      {groups.map((group) => (
        <div
          key={group.id}
          className="border border-[#E6E9F0] dark:border-[#30363d] rounded-lg p-4 bg-white dark:bg-[#161b22] space-y-2"
          style={{ borderLeftColor: group.color || '#6366f1', borderLeftWidth: 4 }}
        >
          <div className="flex items-start gap-2">
            <TagIcon className="h-4 w-4 text-[#8898aa] mt-1 shrink-0" />
            <div className="flex-1 space-y-2">
              <input
                className="w-full font-medium border-b border-transparent focus:border-[#635BFF] outline-none bg-transparent text-[#0A2540] dark:text-[#e6edf3]"
                value={group.name}
                onChange={(e) =>
                  setGroups((list) =>
                    list.map((g) => (g.id === group.id ? { ...g, name: e.target.value } : g))
                  )
                }
              />
              <div className="flex flex-wrap gap-2">
                <input
                  className="flex-1 min-w-[120px] text-xs border border-[#E6E9F0] dark:border-[#30363d] rounded px-2 py-1 bg-white dark:bg-[#0d1117]"
                  placeholder="slug-url"
                  value={group.slug}
                  onChange={(e) =>
                    setGroups((list) =>
                      list.map((g) => (g.id === group.id ? { ...g, slug: e.target.value } : g))
                    )
                  }
                />
                <input
                  type="color"
                  className="h-8 w-12 rounded border border-[#E6E9F0] dark:border-[#30363d] cursor-pointer"
                  value={group.color || '#6366f1'}
                  onChange={(e) =>
                    setGroups((list) =>
                      list.map((g) => (g.id === group.id ? { ...g, color: e.target.value } : g))
                    )
                  }
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeGroup(group.id)}
              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              title="Desactivar grupo"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => saveGroup(group)}
            className="text-sm bg-[#635BFF] text-white px-3 py-1 rounded hover:bg-[#4F46E5]"
          >
            Guardar
          </button>
        </div>
      ))}

      <div className="flex flex-wrap gap-2 items-center border-t border-[#E6E9F0] dark:border-[#30363d] pt-4">
        <input
          className="flex-1 min-w-[200px] border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-3 py-2 text-sm bg-white dark:bg-[#0d1117]"
          placeholder="Nombre del nuevo grupo"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGroup())}
        />
        <button
          type="button"
          onClick={addGroup}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#635BFF] hover:text-[#4F46E5]"
        >
          <PlusIcon className="h-4 w-4" /> Añadir grupo
        </button>
      </div>
    </div>
  );
};

export default InvoiceGroupsSettings;
